import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@components/Button";
import { ChatIcon } from "@components/icons";
import { FormCheckbox } from "@components/inputs/form/FormCheckbox/FormCheckbox";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { useCustomerDetailsQuery } from "@services/customer/queries";
import { useUpdateJointBillServiceSmsSubscriptionMutation } from "@services/jointBillServiceSettings/mutations";
import { type IOverviewJointBillService } from "@services/overview/types";
import { queryClient } from "@/queryClient";
import { useSmsFormSchema } from "./useSmsFormSchema";

interface ISmsForm {
  isActive: boolean;
  phoneNumber: string;
}

interface ISmsModalProps extends IUseModalProps {
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"];
}

export const SmsModal: React.FC<ISmsModalProps> = ({
  jointBillServiceId,
  ...rest
}) => {
  const { t } = useTranslation();
  const modal = useModal(rest);
  const schema = useSmsFormSchema();
  const { data: customerData } = useCustomerDetailsQuery();

  const { mutateAsync: orderSmsMessages, isPending } =
    useUpdateJointBillServiceSmsSubscriptionMutation(jointBillServiceId);

  const { handleSubmit, control, setError } = useForm<ISmsForm>({
    defaultValues: { isActive: false, phoneNumber: customerData?.phone },
    resolver: zodResolver(schema),
  });

  const { setApiErrors } = useApiErrorUtils(setError);

  const submitSmsMessagesOrder = async (data: ISmsForm) => {
    await orderSmsMessages(data)
      .then(() => {
        toast.success(t("smsModal.toast.success"));
        modal.close();
      })
      .catch(setApiErrors);
  };

  const closeModal = () => {
    void queryClient.invalidateQueries({
      queryKey: ["jointBillServiceSettings", jointBillServiceId],
    });

    modal.close();
  };

  return (
    <Modal
      modal={{ ...modal, onClose: closeModal }}
      title={t("smsModal.title")}
      badgeProps={{ icon: ChatIcon, color: "green" }}
      actionButtons={
        <>
          <Button variant="secondary" onClick={closeModal} className="w-full">
            {t("smsModal.buttons.cancel")}
          </Button>
          <Button
            onClick={handleSubmit(submitSmsMessagesOrder)}
            isLoading={isPending}
            className="w-full"
          >
            {t("smsModal.buttons.accept")}
          </Button>
        </>
      }
      className="gap-4"
    >
      <form
        onSubmit={handleSubmit(submitSmsMessagesOrder)}
        className="flex flex-col gap-4"
      >
        <p>{t("smsModal.description")}</p>
        <div className="flex flex-col gap-2">
          <FormCheckbox
            name="isActive"
            control={control}
            label={t("smsModal.form.fields.isActive.label")}
            isLoading={isPending}
          />
          <FormInput
            name="phoneNumber"
            control={control}
            isLoading={isPending}
          />
        </div>
        <p>{t("smsModal.note")}</p>
      </form>
    </Modal>
  );
};
